import { memo } from 'react';
import { Link } from 'react-router-dom';
import { CATEGORY_LABELS, formatDeliveryTime, type Product } from '@kon/shared';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ProductImage } from './ProductImage';
import { RarityChip } from './RarityChip';
import { StockBadge } from './StockBadge';
import styles from './ProductCard.module.css';

interface ProductCardProps {
  product: Product;
  loading?: 'lazy' | 'eager';
}

function ProductCardComponent({ product, loading }: ProductCardProps) {
  const { add } = useCart();
  const { format } = useCurrency();
  const href = `/p/${product.slug}`;
  const outOfStock = product.stock <= 0;

  return (
    <article className={styles.card}>
      <Link to={href} className={styles.media} aria-label={product.name}>
        {product.isFeatured && (
          <span className={styles.featured}>
            <Icon name="star" size={12} /> Featured
          </span>
        )}
        <span className={styles.rarityTop}>
          <RarityChip rarity={product.rarity} size="sm" />
        </span>
        <ProductImage
          imageKey={product.imageKey}
          name={product.name}
          category={product.category}
          rarity={product.rarity}
          loading={loading ?? 'lazy'}
        />
      </Link>

      <div className={styles.body}>
        <span className={styles.category}>{CATEGORY_LABELS[product.category]}</span>
        <h3 className={styles.name}>
          <Link to={href}>{product.name}</Link>
        </h3>
        <div className={styles.meta}>
          <span className={styles.price}>{format(product.priceCents)}</span>
          <StockBadge stock={product.stock} />
        </div>
        <span className={styles.delivery}>
          <Icon name="bolt" size={12} />
          Delivery in ~{formatDeliveryTime(product.deliveryMinutes)}
        </span>
      </div>

      <div className={styles.footer}>
        <Button
          block
          variant={outOfStock ? 'secondary' : 'primary'}
          disabled={outOfStock}
          onClick={() => add(product)}
          aria-label={outOfStock ? `${product.name} is out of stock` : `Add ${product.name} to cart`}
        >
          <Icon name="cart" size={18} />
          {outOfStock ? 'Out of stock' : 'Add to cart'}
        </Button>
      </div>
    </article>
  );
}

export const ProductCard = memo(ProductCardComponent);
